import React, { useState } from "react";
import { Heart, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import CosmicBackground from "../components/astro/CosmicBackground";
import BirthForm, { emptyBirth } from "../components/astro/BirthForm";
import api from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { useBooking } from "../context/BookingContext";

export default function Matching() {
  const { lang } = useLang();
  const { openBooking } = useBooking();
  const [bride, setBride] = useState({ ...emptyBirth });
  const [groom, setGroom] = useState({ ...emptyBirth });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!bride.latitude || !groom.latitude || !bride.date || !groom.date || !bride.time || !groom.time) {
      toast.error("Please complete both birth details, including place from the suggestions");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/astro/match", { bride: { ...bride, lang }, groom: { ...groom, lang }, lang });
      setResult(r.data);
      setTimeout(() => document.getElementById("match-result")?.scrollIntoView({ behavior: "smooth" }), 250);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete the matching.");
    } finally { setLoading(false); }
  };

  const m = result?.milan;
  const reading = result?.reading;

  return (
    <div className="relative" data-testid="matching-page">
      <Seo
        title="Kundali Matching — 36 Point Guna Milan"
        description="Compare two Kundalis for marriage compatibility: 36-point Ashtakoota Guna Milan, emotional, communication, financial and family compatibility, plus a calm traditional dosha reading."
        path="/astrology/matching"
        keywords={["Kundali matching", "Guna Milan 36 points", "marriage compatibility astrology", "Mangal Dosha matching"]}
      />
      <CosmicBackground />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16">
        <div className="text-center mb-10">
          <span className="badge-gold mb-5 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-serif uppercase tracking-[0.28em]">
            <Heart className="h-3.5 w-3.5" /> Kundali Matching
          </span>
          <h1 className="font-serif text-3xl sm:text-5xl font-bold mb-4">
            <span className="text-gradient-flame">36-point Guna Milan, read with kindness</span>
          </h1>
          <p className="text-sm text-tara-body max-w-2xl mx-auto">
            Ashtakoota compatibility computed from both charts, with emotional, communication, financial and family
            harmony — and a calm, traditional view of Mangal Dosha and its classical cancellations.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <p className="mb-3 text-[11px] font-serif uppercase tracking-[0.24em] text-tara-tealDeep">Bride / first person</p>
            <BirthForm value={bride} onChange={setBride} onSubmit={run} loading={loading} steps={2} idPrefix="bride" cta="Ready" />
          </div>
          <div>
            <p className="mb-3 text-[11px] font-serif uppercase tracking-[0.24em] text-tara-tealDeep">Groom / second person</p>
            <BirthForm value={groom} onChange={setGroom} onSubmit={run} loading={loading} steps={2} idPrefix="groom" cta="Ready" />
          </div>
        </div>

        <div className="mt-8 text-center">
          <button onClick={run} disabled={loading} className="btn-sacred aura-pulse inline-flex items-center gap-2.5 rounded-full px-8 py-4 font-serif font-semibold" data-testid="match-submit-btn">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />} Check compatibility
          </button>
        </div>
      </section>

      <section id="match-result" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {loading && (
          <div className="glass rounded-[1.5rem] p-8 flex items-center gap-3" data-testid="match-loading">
            <Loader2 className="h-5 w-5 animate-spin text-tara-teal" />
            <p className="font-display text-lg text-tara-ink">Comparing both charts…</p>
          </div>
        )}

        {m && (
          <div className="space-y-6" data-testid="match-result">
            <div className="glass-liquid relative overflow-hidden rounded-[1.6rem] p-8">
              <span className="orb orb-red h-56 w-56 -top-20 right-10" />
              <div className="relative grid lg:grid-cols-[auto_1fr] gap-8 items-center">
                <div className="text-center">
                  <p className="font-serif text-6xl font-bold text-gradient-flame" data-testid="match-score">{m.total}</p>
                  <p className="text-xs uppercase tracking-[0.16em] text-tara-muted mt-1">out of {m.max} gunas</p>
                  <p className="mt-3 inline-block rounded-full border border-tara-teal/30 bg-white/70 px-4 py-1.5 text-xs font-semibold text-tara-tealDeep" data-testid="match-verdict">
                    {m.verdict}
                  </p>
                </div>
                <div>
                  <p className="font-display text-lg leading-relaxed text-tara-ink">{reading?.summary}</p>
                  <div className="mt-5 grid sm:grid-cols-2 gap-4">
                    {(m.dimensions || []).map((d) => (
                      <div key={d.label} data-testid={`match-dim-${d.label.split(" ")[0].toLowerCase()}`}>
                        <div className="flex justify-between text-xs text-tara-body mb-1">
                          <span>{d.label}</span><span className="font-semibold">{d.score}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-tara-sand overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-tara-teal to-tara-vermilion transition-all duration-700" style={{ width: `${d.score}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="glass rounded-[1.6rem] p-7 overflow-x-auto" data-testid="match-kootas">
              <h3 className="font-serif text-sm uppercase tracking-[0.18em] text-tara-tealDeep mb-5">Ashtakoota breakdown</h3>
              <table className="w-full min-w-[520px] text-sm">
                <thead><tr className="text-[10px] uppercase tracking-[0.14em] text-tara-muted">
                  {["Koota", "Score", "Max", "What it reads"].map((h) => <th key={h} className="pb-3 text-left font-semibold">{h}</th>)}
                </tr></thead>
                <tbody>
                  {m.kootas.map((k) => (
                    <tr key={k.name} className="border-t border-tara-teal/10">
                      <td className="py-3 font-semibold text-tara-ink">{k.name}</td>
                      <td className="py-3 text-tara-vermilion font-semibold">{k.score}</td>
                      <td className="py-3 text-tara-muted">{k.max}</td>
                      <td className="py-3 text-tara-body">{k.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              {[["Emotional compatibility", reading?.emotional], ["Communication", reading?.communication],
                ["Financial harmony", reading?.financial], ["Family harmony", reading?.family]].map(([label, text]) => text && (
                <div key={label} className="glass rounded-[1.5rem] p-6" data-testid={`match-read-${label.split(" ")[0].toLowerCase()}`}>
                  <h4 className="font-display text-xl font-semibold text-tara-ink mb-2">{label}</h4>
                  <p className="text-sm text-tara-body leading-relaxed">{text}</p>
                </div>
              ))}
            </div>

            <div className="glass-liquid rounded-[1.6rem] p-7" data-testid="match-doshas">
              <h3 className="font-serif text-sm uppercase tracking-[0.18em] text-tara-tealDeep mb-4">Traditional dosha reading</h3>
              <p className="text-sm text-tara-body leading-relaxed mb-4">{reading?.dosha_reading}</p>
              {(m.dosha_notes || []).map((d) => (
                <p key={d.name} className="text-xs text-tara-muted leading-relaxed">{d.note}</p>
              ))}
              {(reading?.remedies || []).length > 0 && (
                <div className="mt-6 grid gap-4 sm:grid-cols-3">
                  {reading.remedies.map((r, i) => (
                    <div key={i} className="rounded-2xl border border-tara-teal/20 bg-white/70 p-5">
                      <p className="font-display text-lg text-tara-ink mb-1.5">{r.title}</p>
                      <p className="text-sm text-tara-body leading-relaxed">{r.text}</p>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => openBooking("kundali-milan", `Kundali Milan for ${bride.name} & ${groom.name}`)}
                      className="btn-sacred mt-7 flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-serif font-semibold"
                      data-testid="match-consult-btn">
                🙏 Consult a Priest for Personalized Guidance
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
